/**
 * Typed endpoint functions for `/api/invitations/*` (docs/APP_PLAN.md
 * §8.1, §12 M5; APP_SPEC §20).
 *
 * `accept`/`decline` carry a fresh `Idempotency-Key` on every call, the
 * same pattern `game-sessions.ts` uses (docs/APP_PLAN.md §8.3: "required
 * on: ... invitation accept/decline ..."). `cancel` does not — the API
 * route does not require one (`apps/api/src/modules/invitations/routes.ts`).
 */
import type { HttpClient } from './http-client.js';
import { buildQueryString } from './query.js';
import type { GameSession, Invitation, PaginationParams } from './types.js';

interface InvitationListEnvelope {
  invitations: Invitation[];
}

interface GameSessionEnvelope {
  session: GameSession;
}

export interface InvitationsApi {
  /** `GET /api/invitations/incoming`. Invitations sent to the current user, awaiting a response. */
  listIncoming(pagination?: PaginationParams): Promise<Invitation[]>;
  /** `GET /api/invitations/outgoing`. Invitations the current user sent, awaiting a response. */
  listOutgoing(pagination?: PaginationParams): Promise<Invitation[]>;
  /** `PATCH /api/invitations/:id` with `{ action: 'accept' }`. Only the named invitee may accept; returns the now-active session. */
  accept(invitationId: string): Promise<GameSession>;
  /** `PATCH /api/invitations/:id` with `{ action: 'decline' }`. Only the named invitee may decline. */
  decline(invitationId: string): Promise<void>;
  /** `POST /api/invitations/:id/cancel`. Only the inviter may cancel their own outgoing invitation. */
  cancel(invitationId: string): Promise<void>;
}

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function createInvitationsApi(http: HttpClient): InvitationsApi {
  return {
    async listIncoming(pagination = {}) {
      const { invitations } = await http.request<InvitationListEnvelope>({
        method: 'GET',
        path: `/invitations/incoming${buildQueryString({ ...pagination })}`,
      });
      return invitations;
    },

    async listOutgoing(pagination = {}) {
      const { invitations } = await http.request<InvitationListEnvelope>({
        method: 'GET',
        path: `/invitations/outgoing${buildQueryString({ ...pagination })}`,
      });
      return invitations;
    },

    async accept(invitationId) {
      const { session } = await http.request<GameSessionEnvelope>({
        method: 'PATCH',
        path: `/invitations/${encodeURIComponent(invitationId)}`,
        body: { action: 'accept' },
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      });
      return session;
    },

    async decline(invitationId) {
      await http.requestMaybeEmpty<never>({
        method: 'PATCH',
        path: `/invitations/${encodeURIComponent(invitationId)}`,
        body: { action: 'decline' },
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      });
    },

    async cancel(invitationId) {
      await http.requestMaybeEmpty<never>({
        method: 'POST',
        path: `/invitations/${encodeURIComponent(invitationId)}/cancel`,
      });
    },
  };
}
